import React, { useEffect, useState, useRef } from "react";
import SVG from "react-inlinesvg";
import { StyledContainer } from "./styles";
import classnames from "classnames";
import { mostCommonWords, examples } from "./constants";
import AnimatedTip from "../AnimatedText";
import { useAppStore } from "../../stores/appStore";
import { useProjectStore } from "../../stores/projectStore";

const MIN_INPUT_LENGTH = 10;

const Commander = ({ handleSubmit, commandModeState, setCommandModeState, isInserting, indented, fontSize, lineHeight, leftBarCollapsed, active, innerRef }) => {
  const [value, setValue] = useState("");
  const [promptTip, setPromptTip] = useState(<p>Type more, pretty please.</p>);
  const [buttonReady, setButtonReady] = useState(false);
  const [typingToggle, setTypingToggle] = useState(false);
  const typingTimer = useRef(null);
  const [loading, setLoading] = useState(false);
  const firstDraftRef = useRef(null);
  const setShowNewDocumentCTAs = useProjectStore((state) => state.setShowNewDocumentCTAs);

  // used for setting specific styles for SW 2.0
  // can be removed once SW 2.0 is the default for everyone
  const isSW2 = useAppStore((state) => state.isSW2);
  const isMac = useAppStore((state) => state.isMac);

  const submit = (e, submitValue, cached) => {
    e.preventDefault();
    if (submitValue.length <= MIN_INPUT_LENGTH) return;
    setLoading(true);
    setCommandModeState("loading"); // how is this state used?
    handleSubmit(submitValue, cached);
  };

  useEffect(() => {
    if (isInserting) {
      setCommandModeState("off");
    }
  }, [isInserting]);

  useEffect(() => {
    updatePromptTip();
  }, [typingToggle]);

  useEffect(() => {
    if (commandModeState === "show") {
      setLoading(false);
    }

    if (commandModeState !== "off") {
      setShowNewDocumentCTAs(false);
    }

    console.log("commandModeState: ", commandModeState);
  }, [commandModeState]);

  useEffect(() => {
    if (innerRef && innerRef.current) {
      innerRef.current.style.height = `${firstDraftRef?.current?.offsetHeight + 150}px`;
    }

    // Reset the height when the component unmounts
    return () => {
      if (innerRef && innerRef.current) {
        innerRef.current.style.height = "";
      }
    };
  }, [commandModeState, firstDraftRef?.current?.offsetHeight]);

  const updatePromptTip = () => {
    if (value === null || value === "") {
      setPromptTip(<p>Type more, pretty please.</p>);
      setButtonReady(false);
    } else {
      const uniqueWordsNow = numberOfUniqueWords(value);
      if (uniqueWordsNow < 3 || uniqueWordsNow === null) {
        setPromptTip(<p>Type more, pretty please.</p>);
        setButtonReady(false);
      } else if (uniqueWordsNow >= 3 && uniqueWordsNow < 8) {
        setPromptTip(
          <p>
            Looking good, keep going! The more detail, <br />
            the better your draft will be."
          </p>
        );
        setButtonReady(true);
      } else if (uniqueWordsNow >= 8 && uniqueWordsNow < 100) {
        setPromptTip(
          <p>
            That's the perfect amount of detail! <br />
            For more tips, <a href="https://www.notion.so/sudowrite/Sudowrite-Guide-a7f14fca0aa14035bc84b04c35d93b14#566e15c3d8ff4b8d94294a9a1a162b5a">read the guide.</a>
          </p>
        );
        setButtonReady(true);
      } else if (uniqueWordsNow >= 100) {
        setPromptTip(
          <p>
            This might be more detail than Sudowrite can handle. <br />
            <a href="https://www.notion.so/sudowrite/Sudowrite-Guide-a7f14fca0aa14035bc84b04c35d93b14#566e15c3d8ff4b8d94294a9a1a162b5a">Read the guide.</a>
          </p>
        );
        setButtonReady(true);
      }
    }
  };

  if (commandModeState === "off") return null;

  // TODO: initial state only used for SW 1.0, not for SB
  // once we make SB (2.0) the default for everyone, this should be removed
  if (commandModeState === "initial" && !isSW2) {
    return (
      <div
        style={{
          color: "#9b9b9b",
          position: "absolute",
          width: "calc(100% - 80px)",
          left: "54px",
          right: "40px",
          top: `${60 + (fontSize * (lineHeight - 1)) / 2}px`,
          zIndex: 1000,
          fontSize: fontSize,
          paddingLeft: indented ? fontSize + 20 : 0,
        }}
        onMouseDown={(e) => {
          e.preventDefault();
        }}
      >
        Start typing or{" "}
        <span
          style={{ cursor: "pointer", textDecoration: "underline" }}
          onClick={(e) => {
            e.stopPropagation();
            setCommandModeState("show");
          }}
        >
          Generate a first draft
        </span>
      </div>
    );
  }

  const numberOfUniqueWords = (input) => {
    if (input === "" || input === null) {
      return null;
    }
    let words;
    if (input.includes(" ")) {
      words = input.split(" ");
      if (words.length > 1) {
        if (typingToggle) {
          words = words.slice(0, -1);
        }
      }
    } else {
      words = [input];
    }
    const uniqueWords = words.filter((word) => !mostCommonWords.includes(word.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "")));
    return uniqueWords.length;
  };

  return (
    <StyledContainer className={classnames({ "left-bar-collapsed": leftBarCollapsed, sw2: isSW2 })}>
      <div
        id="commander"
        key="commander"
        ref={firstDraftRef}
      >
        <div>
          <a
            href="#"
            className="commander-back"
            onClick={() => setCommandModeState("off")}
          >{`<- Go Back to Doc`}</a>
          <h2>Describe what you want a first draft of:</h2>
          <form
            className="commander__form"
            onSubmit={(e) => {
              if (loading) return;
              submit(e, value);
            }}
          >
            <textarea
              autoFocus={true}
              placeholder="Write a 500 word story about a man named Leolo who is walking on the beach one day when a magic coconut falls on his head. He wakes up in a cave, surrounded by shark-people and learns he has gained the power to breathe underwater."
              rows="4"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                //set a new timeout
                // delete any existing timeouts
                clearTimeout(typingTimer.current);
                typingTimer.current = setTimeout(() => {
                  setTypingToggle(!typingToggle);
                  // Update state with new input value here
                }, 750);
              }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && !loading) {
                  submit(e, value);
                }
              }}
            />
            <div className="commander__submit-container">
              <AnimatedTip
                numberOfUniqueWords={numberOfUniqueWords}
                text={promptTip}
              ></AnimatedTip>
              {loading && <span className="spinner commander__submit-spinner"></span>}
              <button
                className="commander__submit-button"
                disabled={!buttonReady || loading || !active}
                type="submit"
                value="Go"
              >
                <span className="icon-wrapper">
                  Go
                  <SVG
                    src={isMac ? "/assets/keyboard-shortcuts/command-key.svg" : "/assets/keyboard-shortcuts/control-key-pc.svg"}
                    aria-hidden="true"
                    height={19}
                    width={21}
                  />
                  <SVG
                    src="/assets/keyboard-shortcuts/enter-key.svg"
                    aria-hidden="true"
                    height={19}
                    width={21}
                  />
                </span>
              </button>
            </div>
          </form>
          <div className="commander__examples">
            <h2>Examples</h2>
            {examples.map((example) => {
              return (
                <CommanderExample
                  key={example.header}
                  header={example.header}
                  text={example.text}
                  note={example.note}
                  submit={submit}
                  loading={loading}
                  output={example.output}
                />
              );
            })}
          </div>
        </div>
      </div>
    </StyledContainer>
  );
};

function CommanderExample({ header, text, note, submit, loading, output }) {
  return (
    <div className="commander__example">
      <h3>{header}</h3>
      <p>
        {text} {note}{" "}
        <a
          className="commander__example-action"
          onClick={(e) => {
            if (loading) return;
            submit(e, text, output);
          }}
        >
          Try this →
        </a>
      </p>
    </div>
  );
}

export default Commander;
